import React, { useCallback, useEffect, useState } from "react";
import CurrentDateAndTime from "../../atoms/CurrentDateAndTime/CurrentDateAndTime";
import DiagnosisText, {
  DisagnosisTextType,
} from "../DiagnosisText/DiagnosisText";
import { SidebarMenuItemType } from "../../../services/helpers/types";
import { ReportBindingType } from "../../organisms/Form/Form";
import { mapValuesFromBindsToString } from "./helpers";
import Button from "../../atoms/Button/Button";
import {
  handleCopy,
  isThisArrayNotEmpty,
} from "../../../services/helpers/commonHelpers";

import { useDispatch, useSelector } from "react-redux";
import ReportText from "../../atoms/ReportText/ReportText";
import { setIsReportLoading } from "../../../redux/reducers/loadingReducer";
import useIsEditMode from "../../../services/hooks/useEditMode";
import EditModeDoctorsReport from "../EditModeDoctorsReport/EditModeDoctorsReport";
import { generateReport } from "../../../services/api/report.api";
import { RootState } from "../../../redux/store/store";
import { useTranslation } from "react-i18next";
import AiAssistantDrawer from "../../organisms/AiAssistantDrawer/AiAssistantDrawer";
import { setIsAiAssistantDrawerOpen } from "../../../redux/reducers/appReducer";
import AssistantIcon from "@mui/icons-material/Assistant";
import { CopyAll } from "@mui/icons-material";
import SummarizeIcon from "@mui/icons-material/Summarize";
import ProtectedComponent from "../../atoms/ProtectedComponent/ProtectedComponent";

interface DoctorsReportProps {
  sidebarItem: SidebarMenuItemType;
  reportBinds: ReportBindingType[];
  onDiagnosisTextChange: (type: DisagnosisTextType, value: string) => void;
}

const DoctorsReport = (props: DoctorsReportProps) => {
  const { sidebarItem, reportBinds, onDiagnosisTextChange } = props;

  const userSubscription = useSelector(
    (state: RootState) => state.settings.user?.subscription,
  );
  const isTabletView = window.innerWidth < 1440;

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isInEditMode = useIsEditMode();

  const [report, setReport] = useState("");
  const [formattedReport, setFormattedReport] = useState("");
  const [isAiDialogOpen, setIsAiDialogOpen] = useState(false);
  const [aiReport, setAiReport] = useState<string>("");
  const [withReport, setWithReport] = useState(true);
  const [widthRefElID, setWidthRefElID] = useState("dui-main-page-content");

  const aiDialogOpenInStore = useSelector(
    (state: RootState) => state.app.isAiAssistantDrawerOpen,
  );

  const canUseAiAssistant =
    userSubscription === "pro" ||
    userSubscription === "free" ||
    userSubscription === "premium";

  useEffect(() => {
    if (aiDialogOpenInStore !== isAiDialogOpen) {
      setIsAiDialogOpen(aiDialogOpenInStore);
    }
  }, [aiDialogOpenInStore, isAiDialogOpen]);

  const removeCharAfterWord = (word: string, sentence: string) => {
    const wordIndex = sentence.indexOf(word);
    if (wordIndex === -1) {
      return sentence;
    }
    const charToRemoveIndex = wordIndex + word.length;

    const newSentence =
      sentence.slice(0, charToRemoveIndex) +
      sentence.slice(charToRemoveIndex + 1);

    return newSentence;
  };

  const addNewLineBeforeWord = (word: string, sentence: string): string => {
    const regex = new RegExp(`(${word})`, "g");
    return sentence.replace(regex, `\n\n$1`);
  };

  const removeEmptyLines = (input: string): string => {
    const parts = input.split(" , ").map((part) => part?.trim());
    const formattedString = parts.filter((part) => part !== "").join("\n\n");

    return formattedString;
  };

  const handleFormatReport = useCallback(
    (currentReport: string) => {
      const formattedArray = currentReport.split("\n");
      if (isThisArrayNotEmpty(formattedArray)) {
        const filtered = formattedArray.filter((value) => value !== "");

        const joined = filtered.join(", ");
        const removedSubjective = removeCharAfterWord("Subjektívne:", joined);
        const removedObjective = removeCharAfterWord(
          "Objektívne:",
          removedSubjective,
        );
        const removedMenuItemName = removeCharAfterWord(
          `${sidebarItem.name}:`,
          removedObjective,
        );
        const spacedObjective = addNewLineBeforeWord(
          "Objektívne:",
          removedMenuItemName,
        );
        const spacedSubjective = addNewLineBeforeWord(
          "Subjektívne:",
          spacedObjective,
        );

        const finalFormated = removeEmptyLines(spacedSubjective);

        setFormattedReport(finalFormated);
      }
    },
    [sidebarItem.name],
  );

  useEffect(() => {
    if (reportBinds.length > 0) {
      const filteredWithHiddenFieldsReport = reportBinds.filter(
        (item) => item.visible !== false,
      );
      const report = mapValuesFromBindsToString(
        t,
        filteredWithHiddenFieldsReport,
      );
      const finalReport = `${sidebarItem.name}:\n${report}`;
      setReport(finalReport);
      handleFormatReport(finalReport);
      dispatch(setIsReportLoading(false));
    } else {
      setReport("");
      handleFormatReport("");
      dispatch(setIsReportLoading(false));
    }
  }, [dispatch, handleFormatReport, reportBinds, sidebarItem.name, t]);

  const handleGenerateGeminiAIReport = async () => {
    setAiReport("");

    setIsAiDialogOpen(true);
    dispatch(setIsAiAssistantDrawerOpen(true));

    const aiReport = await generateReport(dispatch, formattedReport);

    setAiReport(aiReport.report);
  };

  const handleDialogClose = () => {
    setIsAiDialogOpen(false);
    dispatch(setIsAiAssistantDrawerOpen(false));
    setAiReport("");
  };

  const handleAIAssistantClick = () => {
    setWidthRefElID("dui-doctors-report");
    setWithReport(false);
    setAiReport("chat");
    setIsAiDialogOpen(true);
    dispatch(setIsAiAssistantDrawerOpen(true));
  };

  const handleAiAssistantClickWithReport = () => {
    setWidthRefElID("dui-main-page-content");
    setWithReport(true);
    handleGenerateGeminiAIReport();
  };

  if (isInEditMode) {
    return (
      <EditModeDoctorsReport
        warningText={sidebarItem.warningText}
        adviseText={sidebarItem.adviseText}
        onDiagnosisTextChange={onDiagnosisTextChange}
      />
    );
  }

  return (
    <div className="dui-doctors-report" id="dui-doctors-report">
      <h1 className="dui-form-fields-title">{t("doctorReportTitle")}</h1>
      <div className="dui-doctors-report-subtitle">
        <CurrentDateAndTime />
        <DiagnosisText
          warningText={sidebarItem.warningText}
          adviseText={sidebarItem.adviseText}
        />
      </div>
      <div className="dui-doctors-report-content">
        <ReportText report={report} />
        <div className="dui-doctors-report-actions">
          <Button
            onClick={() => handleCopy(dispatch, formattedReport)}
            icon={<CopyAll />}
          >
            {t("copy")}
          </Button>
          <ProtectedComponent role="admin">
            {canUseAiAssistant && (
              <div className="dui-doctors-report-actions-pro">
                <Button
                  className="dui-generate-button"
                  onClick={handleAiAssistantClickWithReport}
                  icon={<SummarizeIcon />}
                >
                  {t("generateAiReport")}
                </Button>
                <Button
                  type="secondary"
                  icon={<AssistantIcon />}
                  onClick={handleAIAssistantClick}
                  className="dui-ai-assistant-button"
                >
                  {t("aiAssistantWithReport")}
                </Button>
              </div>
            )}
          </ProtectedComponent>
        </div>
      </div>
      <AiAssistantDrawer
        isOpen={isAiDialogOpen}
        withReport={withReport}
        aiReport={aiReport}
        formattedReport={formattedReport}
        onClose={() => handleDialogClose()}
        setAiReport={setAiReport}
        widthRefElID={isTabletView ? "dui-main-page" : widthRefElID}
        hasCollapseButton={isTabletView ? false : !withReport}
        sidebarItemName={sidebarItem.name}
      />
    </div>
  );
};

export default DoctorsReport;
